import { APP_LOCALE } from '@/utils/constants/locale'
import { HOUR_MS, MINUTE_MS, SECOND_MS } from '@/utils/constants/time'

/**
 * Calculates the relative time elapsed between the current date and a given ISO date,
 * returning a human-readable string in Spanish.
 *
 * The function returns phrases like "Hace unos segundos", "Hace X minutos", "Hace un año", etc.,
 * depending on the time difference between the current time and the input ISO date.
 *
 * @param {Date} dateInput -The Date object to format.
 * @returns {string} A string in Spanish representing how long ago the date occurred.
 *
 * @example
 * const date = "2023-01-12T00:39:54.000Z";
 * console.log(timeElapsed(date));
 * // Output: "Hace 9 meses" (assuming current date is October 2024)
 */
export const timeElapsed = (dateInput: Date | string): string => {
  const now = new Date()
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput

  const dateInputSeconds = date.getTime() / 1000

  const secondsElapsed = Math.floor(now.getTime() / 1000 - dateInputSeconds)

  const minutes = Math.floor(secondsElapsed / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)
  const weeks = Math.floor(days / 7)
  const months = Math.floor(days / 30) // Approximation
  const years = Math.floor(days / 365) // Approximation

  if (secondsElapsed < 60) {
    return 'Hace unos segundos'
  } else if (minutes === 1) {
    return 'Hace un minuto'
  } else if (minutes < 60) {
    return `Hace ${minutes} minutos`
  } else if (hours === 1) {
    return 'Hace una hora'
  } else if (hours < 24) {
    return `Hace ${hours} horas`
  } else if (days === 1) {
    return 'Ayer'
  } else if (days < 7) {
    return `Hace ${days} días`
  } else if (weeks === 1) {
    return 'Hace una semana'
  } else if (weeks < 5) {
    return `Hace ${weeks} semanas`
  } else if (months === 1) {
    return 'Hace un mes'
  } else if (months < 12) {
    return `Hace ${months} meses`
  } else if (years === 1) {
    return 'Hace un año'
  } else {
    return `Hace ${years} años`
  }
}

/**
 * Converts a Date object to a formatted string in Spanish.
 *
 * The date can be formatted in two ways:
 * - `"20/dic/2021"` (without time)
 * - `"20/dic/2021 05:14pm"` (with time)
 *
 * The function uses abbreviated month names in Spanish and provides an option to include the time in 12-hour format with AM/PM notation.
 *
 * @param {Date} date - The Date object to format.
 * @param {boolean} [includeTime=false] - Optional parameter to include time in the output. If true, time will be added in the format `hh:mm(am/pm)`.
 * @returns {string} A formatted date string in Spanish, with or without time based on the `includeTime` flag.
 *
 * @example
 * const date = new Date("2021-12-20T17:14:00.000Z");
 *
 * console.log(formatDate(date));
 * // Output: "20/dic/2021"
 *
 * console.log(formatDate(date, true));
 * // Output: "20/dic/2021 5:14pm"
 */
export const formatDate = (date: Date, includeTime: boolean = false): string =>
  date.toLocaleDateString(APP_LOCALE, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(includeTime && {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    })
  })

/**
 * Safely parses and formats a date string or Date object.
 *
 * This function is useful when working with data that may contain dates as strings
 * (e.g., from APIs) or possibly invalid/missing values.
 * It returns a formatted date string in Spanish or a fallback placeholder if the input is invalid.
 *
 * @param {string | Date | undefined | null} date - The date to parse and format.
 * Can be a string, a Date object, or null/undefined.
 * @param {boolean} [includeTime=false] - Optional flag to include time in the output.
 * If true, time will be included as `hh:mm(am/pm)`.
 * @returns {string} A formatted date string in Spanish, or "—" if the input is invalid.
 *
 * @example
 * formatSafeDate("2023-10-22T12:00:00Z") // "22/oct/2023"
 * formatSafeDate(new Date(), true) // "22/oct/2023 1:00pm"
 * formatSafeDate(null) // "—"
 */
export const formatSafeDate = (date: string | Date | undefined | null, includeTime: boolean = false): string => {
  if (!date) return '—'

  const parsedDate = typeof date === 'string' ? new Date(date) : date

  if (!(parsedDate instanceof Date) || isNaN(parsedDate.getTime())) {
    return '—'
  }

  return formatDate(parsedDate, includeTime)
}

/**
 * Parses a date string in YYYY-MM-DD format as a local date without timezone conversion.
 *
 * This function prevents the common issue where `new Date('2026-01-01')` is interpreted
 * as midnight UTC, which can result in the previous day when converted to local time
 * in negative UTC timezones (e.g., UTC-6).
 *
 * Accepts either a date-only string (YYYY-MM-DD) or a civil datetime string
 * (YYYY-MM-DDTHH:mm:ss, e.g. product-price-link valid_from/valid_to) — the time
 * portion, if present, is ignored.
 *
 * @param dateString - Date or civil datetime string in ISO format
 * @returns Date object representing the date in local timezone
 *
 * @example
 * // Instead of this (which causes timezone issues):
 * new Date('2026-01-01') // Returns Dec 31, 2025 in UTC-6
 *
 * // Use this:
 * parseLocalDate('2026-01-01') // Returns Jan 1, 2026 in local time
 * parseLocalDate('2026-01-01T10:00:00') // Also returns Jan 1, 2026 in local time
 */
export const parseLocalDate = (dateString: string): Date => {
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number)

  return new Date(year, month - 1, day) // month - 1 because Date uses 0-11 for months
}

/**
 * Formats a date in a human-readable full format in Spanish.
 *
 * Returns the date in format "01 de Enero del 2026" with the month name
 * capitalized and the day zero-padded.
 *
 * @param {Date} date - The Date object to format.
 * @returns {string} A formatted date string like "01 de Enero del 2026".
 *
 * @example
 * const date = new Date(2026, 0, 1);
 * console.log(formatFullDate(date));
 * // Output: "01 de Enero del 2026"
 */
export const formatFullDate = (date: Date): string => {
  const day = date.getDate().toString().padStart(2, '0')
  const month = date.toLocaleDateString(APP_LOCALE, { month: 'long' })
  const year = date.getFullYear()
  const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1)

  return `${day} de ${capitalizedMonth} del ${year}`
}

/**
 * Converts a datetime-local string (YYYY-MM-DDTHH:mm) or date string (YYYY-MM-DD)
 * to the backend's civil-time contract (YYYY-MM-DDTHH:mm:ss, no Z/offset — wall-clock
 * local to the destination store, never a UTC instant). No-op if the value already
 * has seconds or is empty.
 *
 * @param value - Date/datetime string to convert
 * @returns Civil datetime string (YYYY-MM-DDTHH:mm:ss)
 */
export const toISODateTime = (value: string): string => {
  if (!value) return value
  if (value.length === 16) return `${value}:00`
  if (value.length === 10) return `${value}T00:00:00`

  return value
}

/**
 * Converts a full ISO 8601 datetime string to a datetime-local compatible value (YYYY-MM-DDTHH:mm).
 *
 * Useful for pre-filling `<input type="datetime-local">` fields with values coming from the API,
 * which typically return full ISO strings (e.g., "2026-03-01T10:00:00.000Z").
 * Returns an empty string if the input is null or undefined.
 *
 * @param {string | null | undefined} iso - Full ISO datetime string from the API.
 * @returns {string} A datetime-local string in "YYYY-MM-DDTHH:mm" format, or "" if input is falsy.
 *
 * @example
 * toDatetimeLocal("2026-03-01T10:00:00.000Z") // "2026-03-01T10:00"
 * toDatetimeLocal(null)                        // ""
 * toDatetimeLocal(undefined)                   // ""
 */
export const toDatetimeLocal = (iso: string | null | undefined): string => (iso ? iso.slice(0, 16) : '')

// «hace unos segundos», «hace 2 min», «hace 3 h»; recibe la hora actual para que un reloj que avanza solo lo renueve.
export const formatElapsed = (from: Date, now: number) => {
  const elapsed = Math.max(0, now - from.getTime())

  if (elapsed < 45 * SECOND_MS) return 'hace unos segundos'

  if (elapsed < HOUR_MS) return `hace ${Math.max(1, Math.round(elapsed / MINUTE_MS))} min`

  return `hace ${Math.round(elapsed / HOUR_MS)} h`
}

export const formatTime = (date: Date) =>
  date.toLocaleTimeString(APP_LOCALE, { hour: '2-digit', minute: '2-digit', second: '2-digit' })

// Día en la hora local del navegador (AAAA-MM-DD).
export const toLocalIsoDate = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`

export const daysBefore = (date: Date, days: number) => {
  const result = new Date(date)

  result.setDate(result.getDate() - days)

  return toLocalIsoDate(result)
}
